import Card from '@/components/Card';
import { Input } from '@/components/Controls/Input';
import { Datatable } from '@/components/Datatable/Datatable';
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { TableViewProps } from '@/type/listing.types';
import { useAuth, useUserRole } from '@/hooks/auth.hooks';
import { RoleName } from '@/api/entities/user-role-type';
import { TableTitle } from '@/components/Header/TableTitle';
import Loader from '@/components/Loading/Loader';
import { Select } from '@/components/Controls/Select';
import { getOptionsStatusFilterV2 } from '@/utils/constant.utils';
import ButtonAdvancedFilter from '@/components/Custom/Button/ButtonAdvancedFilter';
import { VehicleFilter } from '@/components/Custom/Filter/VehicleFilter';
import { useListAllGroups } from '@/hooks/group.hooks';
import { useListAllCompanies } from '@/hooks/company.hooks';
import { mapFilterSelect } from '@/utils/map.utils';
import { useVehicleFilter } from '@/hooks/filters/vehicle-filter.hooks';
import { ClearButton } from '@/components/Custom/Button/ClearButton';
import { VehiculeImportModal } from '@/components/Custom/Modal/VehiculeImportModal';

const limit = 20;

export const VehiculeTableView = <TSchema extends FieldValues>({
  t,
  useGetAllData,
  onGetTotalActive,
  tableHeaders: TableHeaders,
  tableData: TableData,
  pendingDeactivate,
  onDeactivate,
}: TableViewProps<TSchema>) => {
  const { loggedInUser, primaryRole } = useAuth();
  const { isAdmin, isGroupManager, isCompanyManager } = useUserRole();
  const [totalPages, setTotalPages] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  /**
   * === Filter Section ===
   */
  const {
    debouncedFilter,
    setDebouncedFilter,
    filtersEmpty,
    clearFilters,
    key,
    status,
    startDate,
    endDate,
    requestInspection,
    groupId,
    companyId,
    debounceFilterUpdate,
    searchByStatus,
    searchBeginDate,
    searchEndDate,
    searchByRequestInspect,
    searchByGroup,
    searchByCompany,
  } = useVehicleFilter({ setCurrentPage });

  useEffect(() => {
    setDebouncedFilter((prev) => ({
      ...prev,
      role: primaryRole !== RoleName.ADMIN ? primaryRole : undefined,
      userId: loggedInUser?.id,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryRole, loggedInUser?.id]);
  /**
   * === END OF Filter Section ===
   */

  /**
   * === Data Fetching section ===
   */
  const {
    data: allData,
    isLoading: isLoadingAllData,
    isPending: isPendingAllData,
  } = useGetAllData(
    debouncedFilter,
    currentPage,
    limit,
    sortColumn,
    sortDirection,
  );

  const { data: allGroups, isLoading: isLoadingAllGroups } = useListAllGroups({
    userId: isAdmin ? undefined : loggedInUser?.id,
  });
  const { data: allCompanies, isLoading: isLoadingAllCompanies } =
    useListAllCompanies({
      userId: isAdmin ? undefined : loggedInUser?.id,
      groupId: debouncedFilter?.groupId,
    });
  /**
   * === END OF Data Fetching section ===
   */

  /**
   * === Pagination Section ===
   */
  useEffect(() => {
    setTotalPages(allData?.data?.totalPages);
    onGetTotalActive?.(allData?.data?.['total']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData]);
  /**
   * === END OF Pagination Section ===
   */

  /**
   * === Sorting Section ===
   */
  const handleSort = (column: string) => {
    setSortColumn(column);
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };
  /**
   * === END OF Sorting Section ===
   */

  return (
    <>
      <Card bodyClassName="flex flex-col gap-5">
        <div className="flex items-center gap-5">
          <div className="flex flex-1">
            <TableTitle text={t('common:vehicules')} />
          </div>
          <div className="flex gap-5">
            <Input
              placeholder={t('searchVehicule')}
              type="text"
              name="search"
              autoComplete="off"
              leftIcon={
                <i className="ki-outline ki-magnifier text-lg text-gray-8990A8" />
              }
              inputWrapperClassName="w-[300px]"
              inputClassName="h-10"
              value={key}
              onChange={(e) => debounceFilterUpdate(e.target.value)}
            />
            <Select
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500 max-w-40"
              options={getOptionsStatusFilterV2(t)}
              value={status}
              onChange={(e) => searchByStatus(e.target.value)}
            />
            <ButtonAdvancedFilter>
              <VehicleFilter
                t={t}
                isLoadingAllGroups={isLoadingAllGroups}
                isLoadingAllCompanies={isLoadingAllCompanies}
                groupOptions={mapFilterSelect(allGroups?.data)}
                companyOptions={mapFilterSelect(allCompanies?.data)}
                displayGroupFilter={isAdmin || isGroupManager}
                displayCompanyFilter={
                  isAdmin || isGroupManager || isCompanyManager
                }
                startDate={startDate}
                endDate={endDate}
                requestInspection={requestInspection}
                groupId={groupId}
                companyId={companyId}
                searchBeginDate={searchBeginDate}
                searchEndDate={searchEndDate}
                searchByRequestInspect={searchByRequestInspect}
                searchByGroup={searchByGroup}
                searchByCompany={searchByCompany}
              />
            </ButtonAdvancedFilter>
            <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
          </div>
        </div>
        <div>
          <Datatable
            t={t}
            headers={
              <TableHeaders
                t={t}
                onSort={handleSort}
                sortDirection={sortDirection}
              />
            }
            data={
              isLoadingAllData ? (
                <></>
              ) : !allData?.data?.data?.length ? null : (
                <TableData
                  t={t}
                  data={allData?.data?.data}
                  pendingDeactivate={pendingDeactivate}
                  onDeactivate={onDeactivate}
                />
              )
            }
            showPagination={allData?.data?.data?.length > 0 && totalPages > 1}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </Card>
      <VehiculeImportModal id="vehicule-import-modal" t={t} />
      {(isLoadingAllData || isPendingAllData || pendingDeactivate) && (
        <Loader />
      )}
    </>
  );
};
